// Utility function to remove common prefixes and suffixes from filenames
export const simplifyFilenames = (filenames: string[]): string[] => {
  if (filenames.length < 2) {
    return filenames;
  }

  // Split filenames by common delimiters to find common segments
  const delimiterRegex = /[-_\s.]+/;
  const segmentsList = filenames.map(f => f.split(delimiterRegex).filter(s => s.length > 0));
  
  // Helper function to check if segments at a position are numeric variations of each other
  const areNumericVariations = (segments: string[]): boolean => {
    if (segments.length < 2) return false;
    
    // Check if all segments contain numbers
    const allHaveNumbers = segments.every(seg => /\d/.test(seg));
    if (!allHaveNumbers) return false;
    
    // Check if segments differ only in their numeric parts
    // Extract non-numeric parts and see if they match
    const patterns = segments.map(seg => seg.replace(/\d+/g, ''));
    const basePattern = patterns[0];
    
    // All segments should have the same non-numeric structure
    if (!patterns.every(p => p === basePattern)) return false;
    
    // Verify they're actually different (not all the same)
    const allSame = segments.every(seg => seg === segments[0]);
    const result = !allSame; // Return true only if they differ
    
    if (result) {
      console.log('[Filename Simplification] Detected numeric variations:', segments, 'patterns:', patterns);
    }
    
    return result;
  };
  
  // Find common prefix segments (segments that match at the start)
  // Handle numeric variations by continuing past segments that differ only in numbers
  // BUT: stop if we encounter a numeric variation after we've found common segments
  // (this prevents including file extensions that come after numeric suffixes)
  let commonPrefixSegments: string[] = [];
  const minSegments = Math.min(...segmentsList.map(s => s.length));
  
  for (let i = 0; i < minSegments; i++) {
    const segmentsAtPosition = segmentsList.map(segments => segments[i]);
    
    if (segmentsList.every(segments => segments[i] === segmentsAtPosition[0])) {
      // Exact match - add to common prefix
      console.log('[Filename Simplification] Exact match at position', i, ':', segmentsAtPosition[0]);
      commonPrefixSegments.push(segmentsAtPosition[0]);
    } else if (areNumericVariations(segmentsAtPosition)) {
      // Numeric variation detected
      if (commonPrefixSegments.length > 0) {
        // We've already found common segments, so this numeric variation indicates
        // the start of unique parts (like numeric suffixes). Stop here.
        console.log('[Filename Simplification] Stopping at numeric variation after common segments at position', i, ':', segmentsAtPosition);
        break;
      } else {
        // Numeric variation at the start - skip it but continue looking for common segments
        console.log('[Filename Simplification] Skipping numeric variation at position', i, ':', segmentsAtPosition);
        continue;
      }
    } else {
      // Real difference found - stop here
      console.log('[Filename Simplification] Real difference at position', i, ':', segmentsAtPosition);
      break;
    }
  }
  
  console.log('[Filename Simplification] Common prefix segments:', commonPrefixSegments);

  // Find common suffix segments (segments that match at the end)
  // Handle numeric variations similar to prefix
  let commonSuffixSegments: string[] = [];
  const maxSuffixSegments = minSegments - commonPrefixSegments.length;
  
  for (let i = 1; i <= maxSuffixSegments; i++) {
    const segmentsAtPosition = segmentsList.map(segments => segments[segments.length - i]);
    const firstSegment = segmentsAtPosition[0];
    
    if (segmentsList.every(segments => segments[segments.length - i] === firstSegment)) {
      // Exact match - add to common suffix
      commonSuffixSegments.unshift(firstSegment);
    } else if (areNumericVariations(segmentsAtPosition)) {
      // Numeric variation detected - skip this segment but continue looking
      continue;
    } else {
      // Real difference found - stop here
      break;
    }
  }

  // Reconstruct the common prefix string from segments
  // We need to find the delimiter pattern used in the original filenames
  // If we skipped numeric segments at the start, we need to find where the first common segment appears in all filenames
  let bestPrefix = '';
  if (commonPrefixSegments.length > 0) {
    // Find where the first common segment appears in each filename
    const firstCommonSegment = commonPrefixSegments[0];
    const segmentPositions: number[] = [];
    
    for (const filename of filenames) {
      const pos = filename.indexOf(firstCommonSegment);
      if (pos >= 0) {
        segmentPositions.push(pos);
      }
    }
    
    if (segmentPositions.length === filenames.length && segmentPositions.every(p => p > 0)) {
      // All filenames have the first common segment starting after position 0
      // This means we skipped some segments at the start (likely numeric)
      // Start the prefix from the first common segment (including delimiter before it if any)
      const firstFilename = filenames[0];
      const firstSegmentPos = segmentPositions[0];
      
      // Include delimiter before the first common segment if it exists
      let prefixStart = firstSegmentPos;
      if (prefixStart > 0) {
        let delimiterStart = prefixStart - 1;
        while (delimiterStart >= 0 && delimiterRegex.test(firstFilename[delimiterStart])) {
          delimiterStart--;
        }
        prefixStart = delimiterStart + 1;
      }
      
      bestPrefix = firstFilename.substring(prefixStart, firstSegmentPos + firstCommonSegment.length);
      
      // Continue building the rest of the prefix
      let charIndex = firstSegmentPos + firstCommonSegment.length;
      for (let i = 1; i < commonPrefixSegments.length; i++) {
        const segment = commonPrefixSegments[i];
        const segmentStart = firstFilename.indexOf(segment, charIndex);
        if (segmentStart >= 0) {
          bestPrefix = firstFilename.substring(prefixStart, segmentStart + segment.length);
          charIndex = segmentStart + segment.length;
        } else {
          break;
        }
      }
      
      // Ensure prefix ends with a delimiter if there are more segments
      if (charIndex < firstFilename.length) {
        const nextChar = firstFilename[charIndex];
        if (delimiterRegex.test(nextChar)) {
          let delimiterEnd = charIndex;
          while (delimiterEnd < firstFilename.length && delimiterRegex.test(firstFilename[delimiterEnd])) {
            delimiterEnd++;
          }
          bestPrefix = firstFilename.substring(prefixStart, delimiterEnd);
        }
      }
    } else {
      // First common segment starts at position 0, or positions don't match
      // Use the original logic
      const firstFilename = filenames[0];
      let segmentIndex = 0;
      let charIndex = 0;
      
      for (const segment of commonPrefixSegments) {
        const segmentStart = firstFilename.indexOf(segment, charIndex);
        if (segmentStart >= 0) {
          if (segmentIndex === 0) {
            bestPrefix = firstFilename.substring(0, segmentStart + segment.length);
          } else {
            bestPrefix = firstFilename.substring(0, segmentStart + segment.length);
          }
          charIndex = segmentStart + segment.length;
          segmentIndex++;
        } else {
          break;
        }
      }
      
      if (charIndex < firstFilename.length) {
        const nextChar = firstFilename[charIndex];
        if (delimiterRegex.test(nextChar)) {
          let delimiterEnd = charIndex;
          while (delimiterEnd < firstFilename.length && delimiterRegex.test(firstFilename[delimiterEnd])) {
            delimiterEnd++;
          }
          bestPrefix = firstFilename.substring(0, delimiterEnd);
        }
      }
    }
  }

  // Find common suffix string
  let bestSuffix = '';
  if (commonSuffixSegments.length > 0) {
    const firstFilename = filenames[0];
    let searchStart = firstFilename.length;
    
    // Build suffix by finding segments from the end
    for (let i = commonSuffixSegments.length - 1; i >= 0; i--) {
      const segment = commonSuffixSegments[i];
      const segmentStart = firstFilename.lastIndexOf(segment, searchStart);
      if (segmentStart >= 0) {
        if (i === commonSuffixSegments.length - 1) {
          // Last segment - include it and any delimiter after
          let suffixStart = segmentStart;
          // Include delimiter before segment if not at start
          if (suffixStart > 0) {
            let delimiterStart = suffixStart - 1;
            while (delimiterStart >= 0 && delimiterRegex.test(firstFilename[delimiterStart])) {
              delimiterStart--;
            }
            suffixStart = delimiterStart + 1;
          }
          bestSuffix = firstFilename.substring(suffixStart);
        } else {
          // Include delimiter after this segment
          const segmentEnd = segmentStart + segment.length;
          let delimiterEnd = segmentEnd;
          while (delimiterEnd < firstFilename.length && delimiterRegex.test(firstFilename[delimiterEnd])) {
            delimiterEnd++;
          }
          bestSuffix = firstFilename.substring(segmentStart, delimiterEnd) + bestSuffix;
        }
        searchStart = segmentStart - 1;
      } else {
        break;
      }
    }
  }

  console.log('[Filename Simplification] Best prefix:', bestPrefix, 'Best suffix:', bestSuffix);
  
  // Only simplify if we found meaningful common parts (at least 3 characters)
  if (bestPrefix.length >= 3 || bestSuffix.length >= 3) {
    return filenames.map(filename => {
      let simplified = filename;
      
      // Remove common prefix
      if (bestPrefix.length >= 3) {
        if (simplified.startsWith(bestPrefix)) {
          simplified = simplified.substring(bestPrefix.length);
        } else {
          // Try to find the prefix pattern by matching segments
          // Extract extension first to avoid splitting on extension dot
          const lastDotIndex = simplified.lastIndexOf('.');
          const hasExtension = lastDotIndex > 0 && lastDotIndex < simplified.length - 1;
          const extension = hasExtension ? simplified.substring(lastDotIndex) : '';
          const nameWithoutExt = hasExtension ? simplified.substring(0, lastDotIndex) : simplified;
          
          const filenameSegments = nameWithoutExt.split(delimiterRegex).filter(s => s.length > 0);
          
          // Find where the first common segment appears in this filename's segments
          // (it might not be at index 0 if we skipped numeric segments)
          let firstCommonSegmentIndex = -1;
          if (commonPrefixSegments.length > 0) {
            const firstCommonSegment = commonPrefixSegments[0];
            firstCommonSegmentIndex = filenameSegments.findIndex(seg => seg === firstCommonSegment);
          }
          
          if (firstCommonSegmentIndex >= 0 && filenameSegments.length >= firstCommonSegmentIndex + commonPrefixSegments.length) {
            // Check if the segments match starting from firstCommonSegmentIndex
            const matches = commonPrefixSegments.every((seg, idx) => 
              filenameSegments[firstCommonSegmentIndex + idx] === seg
            );
            if (matches) {
              // Reconstruct the prefix from this filename and remove it
              // If we skipped numeric segments at the start, only include from firstCommonSegmentIndex
              // Find the position of the first common segment in the string
              const firstCommonSegment = commonPrefixSegments[0];
              let prefixStart = nameWithoutExt.indexOf(firstCommonSegment);
              
              // Include delimiter before the first common segment if it exists
              if (prefixStart > 0) {
                let delimiterStart = prefixStart - 1;
                while (delimiterStart >= 0 && delimiterRegex.test(nameWithoutExt[delimiterStart])) {
                  delimiterStart--;
                }
                prefixStart = delimiterStart + 1;
              }
              
              // Build prefix end by finding all common segments
              let prefixEnd = prefixStart;
              for (let i = 0; i < commonPrefixSegments.length; i++) {
                const segment = commonPrefixSegments[i];
                const segmentStart = nameWithoutExt.indexOf(segment, prefixEnd);
                if (segmentStart >= 0) {
                  prefixEnd = segmentStart + segment.length;
                  // Include delimiter after segment
                  while (prefixEnd < nameWithoutExt.length && delimiterRegex.test(nameWithoutExt[prefixEnd])) {
                    prefixEnd++;
                  }
                } else {
                  break;
                }
              }
              
              if (prefixEnd > prefixStart && prefixEnd < nameWithoutExt.length) {
                simplified = nameWithoutExt.substring(prefixEnd) + extension;
              }
            }
          }
        }
      }
      
      // Remove common suffix (but preserve file extension)
      if (bestSuffix.length >= 3) {
        // Extract file extension first
        const lastDotIndex = simplified.lastIndexOf('.');
        const hasExtension = lastDotIndex > 0 && lastDotIndex < simplified.length - 1;
        const extension = hasExtension ? simplified.substring(lastDotIndex) : '';
        const nameWithoutExt = hasExtension ? simplified.substring(0, lastDotIndex) : simplified;
        
        if (nameWithoutExt.endsWith(bestSuffix)) {
          simplified = nameWithoutExt.substring(0, nameWithoutExt.length - bestSuffix.length) + extension;
        } else {
          // Try to find the suffix pattern by matching segments
          const nameSegments = nameWithoutExt.split(delimiterRegex);
          if (nameSegments.length >= commonSuffixSegments.length) {
            // Check if the last N segments match
            const matches = commonSuffixSegments.every((seg, idx) => 
              nameSegments[nameSegments.length - commonSuffixSegments.length + idx] === seg
            );
            if (matches) {
              // Find where the suffix starts in the original string
              const lastSegment = commonSuffixSegments[commonSuffixSegments.length - 1];
              const suffixStart = nameWithoutExt.lastIndexOf(lastSegment);
              if (suffixStart >= 0) {
                // Include delimiter before the suffix
                let prefixEnd = suffixStart;
                if (prefixEnd > 0) {
                  let delimiterStart = prefixEnd - 1;
                  while (delimiterStart >= 0 && delimiterRegex.test(nameWithoutExt[delimiterStart])) {
                    delimiterStart--;
                  }
                  prefixEnd = delimiterStart + 1;
                }
                simplified = nameWithoutExt.substring(0, prefixEnd) + extension;
              }
            }
          }
        }
      }
      
      // Ensure we didn't remove too much - if simplified name is too short, use original
      if (simplified.length < 3) {
        return filename;
      }
      
      return simplified;
    });
  }

  return filenames;
};

